import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountInfo,
  AuthenticationResult,
  PublicClientApplication,
} from '@azure/msal-browser';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap, finalize, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import {
  ApiResponse,
  AuthConfig,
  UserInfo,
  LoginData,
} from '../types/auth.types';

/**
 * Servicio que gestiona la autenticación con Azure AD (MSAL) y el consumo de la API
 * para manejar tokens JWT (Access y Refresh).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);

  /**
   * URL base de la API para autenticación
   */
  private readonly apiAuthUrl = environment.apiAuthUrl;

  /**
   * ID del sistema para autenticación.
   */
  private readonly systemId = environment.systemId;

  /**
   * Clave de almacenamiento local para la información del usuario
   */
  private readonly userInfoKey = 'userInfo';

  /**
   * Clave de almacenamiento local para el Access Token
   */
  private readonly accessTokenKey = 'jwtToken';

  /**
   * Clave de almacenamiento local para el Refresh Token
   */
  private readonly refreshTokenKey = 'jwtRefreshToken';

  /**
   * Configuración específica para MSAL (Azure AD)
   */
  private readonly config: AuthConfig = {
    clientId: environment.azure.clientId,
    authority: `https://login.microsoftonline.com/${environment.azure.tenantId}`,
    redirectUri: environment.azure.redirectUri,
    scopes: environment.azure.scopes,
  };

  /**
   * Instancia principal de la librería MSAL
   */
  private readonly msalInstance: PublicClientApplication;

  /**
   * Bandera que indica si MSAL ya ha sido inicializado
   */
  private initialized = false;

  /**
   * Observable compartido para evitar múltiples llamados concurrentes al refresh.
   * Si existe, las peticiones concurrentes se suscriben al mismo flujo.
   */
  private refreshInFlight$: Observable<string> | null = null;

  /**
   * Signal que contiene la información de la cuenta autenticada en MSAL
   */
  public user = signal<AccountInfo | null>(null);

  /**
   * Signal que contiene los datos principales del usuario (nombre, email, roles)
   * provenientes de nuestra API/Backend
   */
  public userInfo = signal<UserInfo | null>(null);

  /**
   * Signal que contiene la lista de roles del usuario
   */
  private readonly userRoles = signal<string[]>([]);

  /**
   * Signal que contiene el mensaje de error de autenticación
   */
  public authError = signal<string | null>(null);

  /**
   * Signal que contiene el indicador de iniciando sesión
   */
  public isSigningIn = signal<boolean>(false);

  /**
   * Constructor del AuthService
   * @param router Enrutador de Angular para redireccionar según estados de auth
   * @param http Cliente HTTP para comunicarse con la API
   */
  constructor() {
    this.initializeTheme();
    this.msalInstance = new PublicClientApplication({
      auth: {
        clientId: this.config.clientId,
        authority: this.config.authority,
        redirectUri: this.config.redirectUri,
        navigateToLoginRequestUrl: true,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: true,
        secureCookies: true,
      },
      system: {
        allowRedirectInIframe: true,
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
      },
    });

    // Inicializa MSAL y luego llama a initializeAuth()
    this.msalInstance.initialize().then(() => {
      this.initialized = true;
      this.initializeAuth();
    });
  }

  private initializeTheme(): void {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) {
      document.documentElement.classList.remove('light');
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add(storedTheme);
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
      localStorage.setItem('theme', 'light');
    }
  }

  /**
   * Realiza las tareas iniciales de autenticación:
   * - Maneja la promesa de redirección de MSAL
   * - Verifica si ya hay cuentas en sesión
   * - Redirecciona a /auth/login si el usuario no está autenticado
   * @private
   */
  private initializeAuth(): void {
    defer(() => this.msalInstance.handleRedirectPromise())
      .pipe(
        tap((result) => {
          if (result) {
            // Si tenemos un resultado de autenticación tras un redirect exitoso
            this.handleAuthenticationSuccess(result);
          } else {
            // Si no hay un resultado de redirección, revisamos si hay cuentas
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              // Si ya hay cuenta en MSAL, la establecemos
              this.user.set(accounts[0]);
              this.tapUserInfo();

              // Si la URL actual es la de login, redireccionamos a home
              if (this.router.url.includes('/auth/login')) {
                this.router.navigate(['/app/home']);
              }
            } else {
              // Si no hay cuentas, solo redirigimos a login si no estamos en rutas públicas
              const currentUrl = this.router.url;
              const isPublicRoute = currentUrl.includes('/auth/');

              if (!isPublicRoute) {
                this.router.navigate(['/auth/login']);
              }
            }
          }
        }),
        catchError((error) => {
          console.error('Error durante la autenticación:', error);

          // Solo redirigir a login si no estamos en rutas públicas
          const currentUrl = this.router.url;
          const isPublicRoute = currentUrl.includes('/auth/');

          if (!isPublicRoute) {
            this.router.navigate(['/auth/login']);
          }

          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  /**
   * Dispara el proceso de inicio de sesión usando redirección de Azure AD (MSAL).
   * @returns Observable<void>
   */
  public login(): Observable<void> {
    if (!this.initialized) {
      return throwError(() => new Error('MSAL no se ha inicializado aún'));
    }

    // Devolvemos un Observable que, al suscribirse, inicia la redirección
    return defer(() => {
      this.msalInstance.loginRedirect({ scopes: this.config.scopes });
      // Después de llamar a loginRedirect, se interrumpe el flujo para el usuario
      // (la página redirige a Azure AD), pero no queremos que RxJS arroje un error.
      return Promise.resolve();
    });
  }

  /**
   * Cierra la sesión del usuario tanto en nuestra API como en Azure AD con redirección
   * @returns Observable<void>
   */
  public logout(): Observable<void> {
    // Construimos el payload requerido por el backend
    const info = this.getUserInfo() as unknown as { userId?: string } | null;
    const userId = info?.userId ?? '';
    const systemId = this.systemId;

    // Primero avisa a nuestra API que el usuario cerró sesión
    return this.http
      .post<ApiResponse<string>>(`${this.apiAuthUrl}/auth/logout`, {
        userId,
        systemId,
      })
      .pipe(
        tap({
          next: (resp) => {
            // Aunque el backend reporte error, procedemos con el cierre local
            if (!resp?.isSuccess) {
              console.warn(
                `Logout backend no exitoso: ${resp?.message} (status: ${resp?.statusCode})`,
              );
            }
            // Limpiamos todo lo relacionado a la sesión en localStorage y signals
            this.user.set(null);
            this.userInfo.set(null);
            localStorage.removeItem(this.userInfoKey);
            localStorage.removeItem(this.accessTokenKey);
            localStorage.removeItem(this.refreshTokenKey);
            this.userRoles.set([]);
          },
        }),
        switchMap(() =>
          defer(() => {
            // Llamamos a logoutRedirect para que Azure AD
            // gestione el cierre de sesión con redirección
            this.msalInstance.logoutRedirect();
            // Retornamos un Promise vacío para que el observable no falle
            return Promise.resolve();
          }),
        ),
        catchError((error) => {
          console.error('Error durante el logout:', error);
          // En caso de error, navegamos a /auth/login
          this.router.navigate(['/auth/login']);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Indica si hay un usuario autenticado en MSAL (cuentas en memoria)
   * @returns true si el usuario está autenticado, false en caso contrario
   */
  public isAuthenticated(): boolean {
    const hasUserInfo = this.getUserInfo() !== null;
    const hasMsalAccounts = this.msalInstance.getAllAccounts().length > 0;
    return hasMsalAccounts && hasUserInfo;
  }

  /**
   * Devuelve la información de usuario guardada en localStorage
   * @returns UserInfo | null - Objeto con la información del usuario o null si no existe o es inválido
   */
  public getUserInfo(): UserInfo | null {
    try {
      const storedData = localStorage.getItem(this.userInfoKey);
      if (!storedData) return null;

      const parsedData = JSON.parse(storedData);

      // Validación básica para asegurar que el objeto cumple con la interfaz UserInfo
      if (typeof parsedData !== 'object' || parsedData === null) return null;

      return parsedData as UserInfo;
    } catch (error) {
      console.error('Error al recuperar información de usuario:', error);
      return null;
    }
  }

  /**
   * Guarda un objeto con la información del usuario en localStorage
   * @param userInfo Objeto con información del usuario (ej. nombre, email, roles)
   */
  public setUserInfo(userInfo: unknown): void {
    localStorage.setItem(this.userInfoKey, JSON.stringify(userInfo));
  }

  /**
   * Devuelve el Access Token (JWT) almacenado en localStorage
   * En modo de desarrollo, usa el token del environment si no hay uno en localStorage
   * @returns string | null
   */
  public getAccessToken(): string | null {
    const storedToken = localStorage.getItem(this.accessTokenKey);
    return storedToken;
  }

  /**
   * Guarda el Access Token (JWT) en localStorage
   * @param token Cadena JWT
   */
  public setAccessToken(token: string): void {
    localStorage.setItem(this.accessTokenKey, token);
  }

  /**
   * Devuelve el Refresh Token almacenado
   * @returns string | null
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Guarda el Refresh Token en localStorage
   * @param token Cadena del Refresh Token
   */
  public setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  /**
   * Invoca el endpoint de nuestra API para refrescar el token usando el Refresh Token
   * @param expiredToken JWT expirado
   * @returns Observable<string> con el nuevo Access Token
   */
  public handleRefreshToken(expiredToken: string): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No existe Refresh Token almacenado'));
    }

    // Si ya hay un refresh en curso, reutilizamos el mismo observable compartido
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const request$ = this.http
      .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/refresh`, {
        accessToken: expiredToken || '',
        refreshToken,
      })
      .pipe(
        switchMap((response) => {
          if (!response?.isSuccess || !response?.data) {
            const msg = response?.message || 'Refresh token inválido o expirado';
            return throwError(() => new Error(msg));
          }
          // Almacenar los tokens
          this.setAccessToken(response.data.token);
          this.setRefreshToken(response.data.refreshToken);
          return defer(() => Promise.resolve(response.data.token));
        }),
        finalize(() => {
          // Al completar o error, liberamos el observable compartido
          this.refreshInFlight$ = null;
        }),
        // Compartimos el resultado entre concurrentes, liberando suscripción al finalizar
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.refreshInFlight$ = request$;
    return request$;
  }

  /**
   * Determina si un token está por expirar pronto (por ejemplo, en 30s)
   * @param token JWT a evaluar
   * @returns true si faltan <= 30s para expirar, false de lo contrario
   */
  public isTokenExpiringSoon(token: string): boolean {
    try {
      // Dividimos el token por '.', el segundo índice (payload) está en Base64
      const payload = token.split('.')[1];
      if (!payload) return true;

      // Decodificamos Base64
      const decoded = JSON.parse(atob(payload));
      const exp = decoded.exp; // exp es tiempo UNIX en segundos

      if (!exp) return true;

      const nowInSec = Math.floor(Date.now() / 1000);
      const secondsLeft = exp - nowInSec;
      const REFRESH_MARGIN = 30; // margen de 30 segundos

      return secondsLeft <= REFRESH_MARGIN;
    } catch (e) {
      console.error('Error al decodificar token:', e);
      // Si falla la decodificación, asumimos que el token no es válido
      return true;
    }
  }

  /**
   * Maneja el proceso posterior a una autenticación exitosa con MSAL.
   * - Establece la cuenta en la Signal 'user'
   * - Adquiere el token de Azure en modo silencioso
   * - Envía el token de Azure a nuestra API para obtener un JWT propio
   * - Decodifica y almacena roles, email y nombre de usuario
   * @param result Objeto con datos del resultado de la autenticación
   * @private
   */
  private handleAuthenticationSuccess(result: AuthenticationResult): void {
    if (!result.account) {
      return;
    }

    // Guardamos la cuenta autenticada en MSAL
    this.user.set(result.account);

    // Extraemos info básica del token de Azure
    const idTokenClaims = result.idTokenClaims as any;
    const name = idTokenClaims.name || idTokenClaims.given_name || '';
    const email =
      idTokenClaims.preferred_username || idTokenClaims.emails?.[0] || '';

    // Obtenemos un token de Azure (silent) para enviarlo a nuestra API
    defer(() =>
      this.msalInstance.acquireTokenSilent({
        scopes: this.config.scopes,
        account: result.account,
      }),
    )
      .pipe(
        switchMap((tokenResponse) => {
          // Enviamos el token de Azure a la API para que nos devuelva nuestro JWT
          // Limpiamos cualquier error previo
          this.authError.set(null);
          this.isSigningIn.set(true);

          return this.http
            .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/login`, {
              tipo: 'Ms365',
              token: tokenResponse.accessToken,
              idSistema: this.systemId,
            })
            .pipe(
              tap((response) => {
                if (!response.isSuccess || !response.data) {
                  const errorMessage =
                    response.message ||
                    'Error al validar credenciales durante el inicio de sesión';

                  this.authError.set(errorMessage);
                  this.user.set(null);
                  this.userInfo.set(null);
                  this.userRoles.set([]);
                  this.router.navigate(['/auth/login']);
                  this.isSigningIn.set(false);
                  throw new Error(errorMessage);
                }

                const { token, refreshToken, profile } = response.data;
                // Almacenar los tokens
                this.setAccessToken(token);
                this.setRefreshToken(refreshToken);

                // Roles: priorizar los roles del perfil de la API; fallback al token de Azure
                let roles: string[] = Array.isArray(profile?.roles)
                  ? (profile!.roles as string[])
                  : [];
                if (!roles || roles.length === 0) {
                  try {
                    const tokenPayload = JSON.parse(
                      atob(result.accessToken.split('.')[1]),
                    );
                    roles = tokenPayload.roles || [];
                  } catch {
                    roles = [];
                  }
                }

                // Actualizamos roles en nuestra Signal
                this.userRoles.set(roles);

                // Información de usuario: priorizar nombre/email del perfil
                const userInfo: UserInfo = {
                  userId:
                    profile?.idUsuario !== undefined
                      ? String(profile.idUsuario)
                      : undefined,
                  name: profile?.nombreUsuario || name,
                  email: profile?.email || email,
                  employeeNumber: profile?.numeroEmpleado
                    ? String(profile.numeroEmpleado)
                    : undefined,
                  roles,
                };

                this.setUserInfo(userInfo);
                this.tapUserInfo();

                this.isSigningIn.set(false);

                // Redirigimos a la página de inicio
                this.router.navigate(['/app/home']);
              }),
              catchError((error) => {
                // Extraemos el mensaje de error de la respuesta
                const errorMessage =
                  'Error al validar token durante el inicio de sesión';

                // Actualizamos el signal de error
                this.authError.set(errorMessage);

                // Limpiamos cualquier información de sesión previa
                this.user.set(null);
                this.userInfo.set(null);
                this.userRoles.set([]);

                // Redirigimos al usuario a la página de login
                this.router.navigate(['/auth/login']);

                this.isSigningIn.set(false);

                // Propagamos el error
                return throwError(() => new Error(errorMessage));
              }),
            );
        }),
        catchError((error) => {
          console.error('Error durante el proceso de autenticación:', error);
          this.userRoles.set([]);

          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  /**
   * Restaura la información del usuario desde localStorage y actualiza los signals
   * @private
   */
  private tapUserInfo(): void {
    const userInfo = this.getUserInfo();
    if (userInfo) {
      this.userInfo.set(userInfo);
      this.userRoles.set(userInfo.roles ?? []);
    }
  }

  /**
   * Verifica si el usuario posee un rol específico
   * @param role Rol a verificar (ej. 'Admin')
   * @returns true si el usuario tiene ese rol, false de lo contrario
   */
  public hasRole(role: string): boolean {
    return this.userRoles().includes(role);
  }
}
